import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Users, MapPin, Clock, Check } from 'lucide-react';
import axios from 'axios';
import { PublicNav, PublicFooter } from './PublicPages';

const API = `${""}/api`;

const ProviderDirectory = () => {
  const [form, setForm] = useState({ name: '', email: '', specialty: '', location: '', interested: false });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) { setError('Email is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/providers/interest`, {
        name: form.name, email: form.email, specialty: form.specialty,
        location: form.location, interested: true
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="provider-directory">
      <PublicNav
        onSignIn={() => window.location.href = '/login'}
        onSignUp={() => window.location.href = '/signup'}
      />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center space-y-6">
          <div className="flex items-center justify-center gap-3">
            <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center">
              <Users className="h-7 w-7 text-teal-600" />
            </div>
          </div>

          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">
              Provider Directory
            </h1>
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-1.5 rounded-full text-sm font-medium">
              <Clock className="h-4 w-4" /> Coming Soon
            </div>
          </div>

          <p className="text-slate-500 max-w-lg mx-auto text-sm leading-relaxed">
            Find a verified peptide therapy practitioner near you. Our Provider Directory launches with the full platform release.
          </p>

          {submitted ? (
            <Card className="max-w-md mx-auto p-6 border-teal-200 bg-teal-50" data-testid="interest-success">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Check className="h-6 w-6 text-teal-600" />
                <span className="font-semibold text-teal-800">Thank You!</span>
              </div>
              <p className="text-sm text-teal-700">We'll notify you when the Provider Directory launches. In the meantime, consider applying to be a Contributing Practitioner for priority listing.</p>
              <Button onClick={() => window.location.href = '/'} variant="outline" className="mt-4 border-teal-300 text-teal-700">
                Back to Home
              </Button>
            </Card>
          ) : (
            <Card className="max-w-md mx-auto p-6" data-testid="interest-form">
              <h3 className="font-semibold text-slate-800 mb-3 text-left">Get Notified When We Launch</h3>
              <form onSubmit={handleSubmit} className="space-y-3 text-left">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                  <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Dr. Jane Smith" data-testid="interest-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                  <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="doctor@practice.com" required data-testid="interest-email" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Specialty</label>
                  <Input value={form.specialty} onChange={e => setForm(f => ({...f, specialty: e.target.value}))} placeholder="Functional Medicine" data-testid="interest-specialty" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Location</label>
                  <Input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} placeholder="City, State" data-testid="interest-location" />
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={form.interested} onChange={e => setForm(f => ({...f, interested: e.target.checked}))} data-testid="interest-checkbox" />
                  <span>I'm interested in being listed in the Provider Directory</span>
                </label>
                {error && <p className="text-red-500 text-xs">{error}</p>}
                <Button type="submit" disabled={submitting} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="interest-submit">
                  {submitting ? 'Submitting...' : 'Notify Me'}
                </Button>
              </form>
            </Card>
          )}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default ProviderDirectory;
